import naja from "naja";

/**
 * Ochrana proti vícenásobnému odeslání AJAX formuláře.
 *
 * Bez této ochrany šlo tlačítko "Uložit / Uložit a zavřít" naklikat víckrát,
 * než se stihl dokončit AJAX požadavek – každý klik pak na serveru vytvořil
 * nový záznam, takže vznikaly 2-3 duplicitní zakázky/tikety se stejným popisem
 * (viz úkol 833).
 *
 * Princip: jakmile submit tlačítko v AJAX formuláři odstartuje požadavek,
 * všechna submit tlačítka daného formuláře dočasně zablokujeme a po dokončení
 * požadavku (úspěch, chyba i přerušení) je zase povolíme.
 *
 * Blokujeme až v události `start`, kdy už má Naja hodnotu klikntého tlačítka
 * zaserializovanou v těle požadavku (viz UIHandler.clickElement) – zablokování
 * DOM prvku tedy neovlivní odeslanou akci formuláře.
 *
 * Pojistky proti trvalému zablokování (aby uživatel nikdy neuvízl):
 *  - blokujeme jen pokud `start` následuje bezprostředně po kliknutí na submit
 *    (klik → start je synchronní; starší "stale" trigger ignorujeme),
 *  - i kdyby událost `complete` z jakéhokoli důvodu nedorazila, po
 *    RELEASE_TIMEOUT_MS tlačítka sami odblokujeme,
 *  - odblokováváme jen tlačítka, která jsme sami zablokovali (dataset.dsgLocked),
 *    nikdy nesaháme na tlačítka zablokovaná aplikační logikou.
 */
const RELEASE_TIMEOUT_MS = 5000;
const TRIGGER_MAX_AGE_MS = 1000;

class DoubleSubmitGuardExtension {
    constructor() {
        this.pendingTrigger = null;
        this.pendingTriggerAt = 0;
        this.releaseTimers = new WeakMap();

        this.handleInteraction = this.handleInteraction.bind(this);
        this.handleStart = this.handleStart.bind(this);
        this.handleComplete = this.handleComplete.bind(this);
    }

    initialize(naja) {
        // interaction nese element, na který uživatel klikl (submit tlačítko)
        naja.uiHandler.addEventListener('interaction', this.handleInteraction);
        naja.addEventListener('start', this.handleStart);
        naja.addEventListener('complete', this.handleComplete);
    }

    now() {
        return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    }

    handleInteraction(event) {
        this.pendingTrigger = event.detail.element || null;
        this.pendingTriggerAt = this.now();
    }

    isGuardedSubmit(el) {
        return el
            && typeof el.matches === 'function'
            && el.matches('input[type="submit"], button[type="submit"]')
            // reload formuláře přes on-change (např. výběr zakázky) nechceme blokovat
            && !el.classList.contains('on-change-skip-validation')
            && el.closest('form.ajax');
    }

    submitButtons(form) {
        return form.querySelectorAll('input[type="submit"], button[type="submit"]');
    }

    lock(form) {
        this.submitButtons(form).forEach((btn) => {
            if (!btn.disabled) {
                btn.disabled = true;
                btn.dataset.dsgLocked = '1';
            }
        });
    }

    unlock(form) {
        this.submitButtons(form).forEach((btn) => {
            if (btn.dataset.dsgLocked === '1') {
                btn.disabled = false;
                delete btn.dataset.dsgLocked;
            }
        });
    }

    clearTimer(form) {
        const timer = this.releaseTimers.get(form);
        if (timer) {
            clearTimeout(timer);
            this.releaseTimers.delete(form);
        }
    }

    handleStart(event) {
        const trigger = this.pendingTrigger;
        const fresh = (this.now() - this.pendingTriggerAt) <= TRIGGER_MAX_AGE_MS;
        this.pendingTrigger = null;

        // blokujeme jen požadavky vyvolané čerstvým kliknutím na submit tlačítko
        if (!fresh || !this.isGuardedSubmit(trigger)) {
            return;
        }

        const form = trigger.closest('form.ajax');
        this.lock(form);
        // start i complete běží uvnitř téhož makeRequest a sdílí stejný options objekt
        event.detail.options._dsgForm = form;

        // Pojistka: kdyby 'complete' z jakéhokoli důvodu nedorazil, po timeoutu odblokuj.
        this.clearTimer(form);
        this.releaseTimers.set(form, setTimeout(() => {
            this.releaseTimers.delete(form);
            this.unlock(form);
        }, RELEASE_TIMEOUT_MS));
    }

    handleComplete(event) {
        const form = event.detail.options && event.detail.options._dsgForm;
        if (!form) {
            return;
        }

        delete event.detail.options._dsgForm;
        this.clearTimer(form);
        this.unlock(form);
    }
}

export default DoubleSubmitGuardExtension;
